/**
 * Édition d'une carte (`GET|PATCH /admin/cards/:id`).
 *
 * Deux validations, dans cet ordre, et c'est voulu :
 * 1. **locale**, avec le schéma du contrat (`adminCardPatchSchema`) — l'administrateur voit
 *    immédiatement quel champ bloque, sans aller-retour réseau ;
 * 2. **serveur**, qui reste seul juge : son refus (`400 VALIDATION_FAILED`) est affiché tel quel
 *    et **la saisie est conservée** — c'est la règle d'ergonomie du livrable.
 *
 * Le `PATCH` n'envoie que les champs modifiés : c'est un patch partiel strict, et envoyer un
 * champ inchangé produirait une ligne d'audit trompeuse.
 */
import { adminCardPatchSchema } from '@elemora/shared/api';
import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { lireCarte, modifierCarte } from '../api/admin';
import { ErreurApi, erreursDeValidation, versErreurApi } from '../api/erreurs';
import { libelleContenu, nomCarte } from '../catalogue/contenu-fr';
import { M } from '../catalogue/messages';
import { useChargement } from '../hooks/use-chargement';
import { Bandeau, BandeauErreur } from '../ui/Bandeau';
import { Bouton } from '../ui/Bouton';
import { Champ } from '../ui/Champ';
import { Chargement } from '../ui/Chargement';
import { Coquille } from '../ui/Coquille';
import { EditeurEffets } from '../ui/EditeurEffets';
import { Panneau } from '../ui/Panneau';
import { Selection } from '../ui/Selection';
import { libelleRarete } from '../utils/presentation';

import type { AdminCardPatch } from '@elemora/shared/api';
import type { CardEffect, Carte, Element, Rarete } from '@elemora/shared/content';

/** Valeurs du formulaire : les nombres restent des chaînes tant qu'on les saisit. */
interface ValeursCarte {
  readonly nameKey: string;
  readonly descKey: string;
  readonly rarete: Rarete;
  readonly element: Element | 'neutre';
  readonly points: string;
  readonly tags: string;
  readonly effets: CardEffect[];
}

const OPTIONS_RARETE = [
  { valeur: 'commune', libelle: M.raretes.commune },
  { valeur: 'rare', libelle: M.raretes.rare },
  { valeur: 'legendaire', libelle: M.raretes.legendaire },
  { valeur: 'mythique', libelle: M.raretes.mythique },
] as const;

const OPTIONS_ELEMENT = [
  { valeur: 'feu', libelle: M.elements.feu },
  { valeur: 'eau', libelle: M.elements.eau },
  { valeur: 'terre', libelle: M.elements.terre },
  { valeur: 'air', libelle: M.elements.air },
  { valeur: 'neutre', libelle: M.elements.neutre },
] as const;

/** Extrait les valeurs éditables d'une carte du serveur. */
function valeursDe(carte: Carte): ValeursCarte {
  return {
    nameKey: carte.nameKey,
    descKey: carte.descKey,
    rarete: carte.rarete,
    element: carte.element,
    points: String(carte.points),
    tags: carte.tags.join(', '),
    effets: carte.effets.map((effet) => ({ ...effet })),
  };
}

/** Découpe la saisie d'étiquettes : virgules, retours à la ligne, espaces superflus. */
function analyserTags(saisie: string): string[] {
  return saisie
    .split(/[,\n]/)
    .map((etiquette) => etiquette.trim())
    .filter((etiquette) => etiquette.length > 0);
}

/** Construit le patch ne contenant que les champs réellement modifiés. */
function construirePatch(initial: Carte, valeurs: ValeursCarte): AdminCardPatch {
  const patch: Record<string, unknown> = {};
  if (valeurs.nameKey !== initial.nameKey) patch['nameKey'] = valeurs.nameKey;
  if (valeurs.descKey !== initial.descKey) patch['descKey'] = valeurs.descKey;
  if (valeurs.rarete !== initial.rarete) patch['rarete'] = valeurs.rarete;
  if (valeurs.element !== initial.element) patch['element'] = valeurs.element;
  const points = Number(valeurs.points);
  if (Number.isFinite(points) && points !== initial.points) patch['points'] = Math.trunc(points);
  const tags = analyserTags(valeurs.tags);
  if (JSON.stringify(tags) !== JSON.stringify(initial.tags)) patch['tags'] = tags;
  if (JSON.stringify(valeurs.effets) !== JSON.stringify(initial.effets)) {
    patch['effets'] = valeurs.effets;
  }
  return patch as AdminCardPatch;
}

/** Indexe un refus serveur par chemin de champ (`effets.0.valeur`), pour l'afficher au bon endroit. */
function erreursParChemin(erreur: ErreurApi): Record<string, string> {
  const resultat: Record<string, string> = {};
  for (const defaut of erreursDeValidation(erreur)) {
    // Le serveur préfixe par le corps (`body.effets.0.valeur`) : on garde le chemin utile.
    const chemin = defaut.chemin.replace(/^(body|query)\./, '');
    resultat[chemin] = defaut.message;
  }
  return resultat;
}

export function EcranCarteEdition() {
  const parametres = useParams<{ id: string }>();
  const identifiant = parametres.id ?? '';
  const chargement = useChargement((signal) => lireCarte(identifiant, signal));

  const [valeurs, setValeurs] = useState<ValeursCarte | null>(null);
  const [erreurs, setErreurs] = useState<Record<string, string>>({});
  const [refus, setRefus] = useState<ErreurApi | null>(null);
  const [succes, setSucces] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);
  const [reference, setReference] = useState<Carte | null>(null);

  // La fiche initialise le formulaire une seule fois par chargement (jamais à chaque rendu :
  // une réinitialisation écraserait la saisie en cours).
  useEffect(() => {
    if (chargement.donnees === null) return;
    setReference(chargement.donnees.card);
    setValeurs(valeursDe(chargement.donnees.card));
    setErreurs({});
    setRefus(null);
  }, [chargement.donnees]);

  const introuvable = chargement.erreur?.code === 'NOT_FOUND';
  const titre = useMemo(
    () =>
      chargement.donnees === null
        ? M.cartes.editionTitre(identifiant)
        : nomCarte(chargement.donnees.card),
    [chargement.donnees, identifiant],
  );

  async function enregistrer(): Promise<void> {
    if (enCours || reference === null || valeurs === null) return;
    setSucces(null);
    setRefus(null);
    setErreurs({});

    const patch = construirePatch(reference, valeurs);
    if (Object.keys(patch).length === 0) {
      setSucces(M.cartes.aucuneModification);
      return;
    }

    const verification = adminCardPatchSchema.safeParse(patch);
    if (!verification.success) {
      const parChemin: Record<string, string> = {};
      for (const probleme of verification.error.issues) {
        parChemin[probleme.path.join('.')] = probleme.message;
      }
      setErreurs(parChemin);
      setRefus(
        new ErreurApi({
          code: 'VALIDATION_FAILED',
          message: M.cartes.validationLocale,
          statutHttp: 0,
          messageServeur: M.cartes.validationLocale,
        }),
      );
      return;
    }

    setEnCours(true);
    try {
      const resultat = await modifierCarte(identifiant, patch);
      setReference(resultat.card);
      setValeurs(valeursDe(resultat.card));
      setSucces(M.cartes.enregistree);
    } catch (inconnu) {
      const erreurApi = versErreurApi(inconnu);
      setRefus(erreurApi);
      setErreurs(erreursParChemin(erreurApi));
    } finally {
      setEnCours(false);
    }
  }

  return (
    <Coquille
      titre={titre}
      sousTitre={
        chargement.donnees === null
          ? M.commun.chargementFiche
          : M.cartes.editionTitre(chargement.donnees.card.id)
      }
      actions={
        <Link className="lien-action" to="/cartes">
          {M.commun.retourListe}
        </Link>
      }
    >
      <Panneau aide={M.avertissements.brouillon}>
        {introuvable ? (
          <Bandeau texte={M.cartes.introuvable(identifiant)} ton="avertissement" />
        ) : null}
        {chargement.erreur === null || introuvable ? null : (
          <BandeauErreur erreur={chargement.erreur} surReessai={chargement.recharger} />
        )}
        {chargement.chargement ? <Chargement texte={M.commun.chargementFiche} /> : null}

        {valeurs === null || reference === null ? null : (
          <form
            className="formulaire"
            onSubmit={(evenement) => {
              evenement.preventDefault();
              void enregistrer();
            }}
          >
            {refus === null ? null : refus.code === 'VALIDATION_FAILED' ? (
              <BandeauErreur erreur={refus} titre={M.cartes.validationLocale} />
            ) : (
              <BandeauErreur erreur={refus} />
            )}
            {succes === null ? null : <Bandeau texte={succes} ton="succes" />}

            <div className="formulaire__grille">
              <Champ
                id="carte-name-key"
                etiquette={M.cartes.nom}
                value={valeurs.nameKey}
                aide={libelleContenu(valeurs.nameKey)}
                erreur={erreurs['nameKey'] ?? null}
                onChange={(evenement) =>
                  setValeurs({ ...valeurs, nameKey: evenement.target.value })
                }
              />
              <Champ
                id="carte-desc-key"
                etiquette={M.cartes.description}
                value={valeurs.descKey}
                aide={libelleContenu(valeurs.descKey)}
                erreur={erreurs['descKey'] ?? null}
                onChange={(evenement) =>
                  setValeurs({ ...valeurs, descKey: evenement.target.value })
                }
              />
              <Selection
                id="carte-rarete"
                etiquette={M.cartes.rarete}
                options={OPTIONS_RARETE}
                value={valeurs.rarete}
                erreur={erreurs['rarete'] ?? null}
                onChange={(evenement) =>
                  setValeurs({ ...valeurs, rarete: evenement.target.value as Rarete })
                }
              />
              <Selection
                id="carte-element"
                etiquette={M.cartes.element}
                options={OPTIONS_ELEMENT}
                value={valeurs.element}
                erreur={erreurs['element'] ?? null}
                onChange={(evenement) =>
                  setValeurs({ ...valeurs, element: evenement.target.value as Element | 'neutre' })
                }
              />
              <Champ
                id="carte-points"
                etiquette={M.cartes.points}
                type="number"
                min="0"
                step="1"
                value={valeurs.points}
                aide={libelleRarete(valeurs.rarete)}
                erreur={erreurs['points'] ?? null}
                onChange={(evenement) => setValeurs({ ...valeurs, points: evenement.target.value })}
              />
              <Champ
                id="carte-tags"
                etiquette={M.cartes.tags}
                value={valeurs.tags}
                aide={M.cartes.tagsAide}
                erreur={erreurs['tags'] ?? null}
                onChange={(evenement) => setValeurs({ ...valeurs, tags: evenement.target.value })}
              />
            </div>

            <p className="formulaire__note">{M.cartes.cumulFixe}</p>

            <EditeurEffets
              effets={valeurs.effets}
              surChangement={(effets) => setValeurs({ ...valeurs, effets })}
            />

            <div className="formulaire__actions">
              <Bouton type="submit" enCours={enCours} texteEnCours={M.commun.enregistrementEnCours}>
                {M.commun.enregistrer}
              </Bouton>
              <button
                type="button"
                className="lien-action"
                onClick={() => setValeurs(valeursDe(reference))}
              >
                {M.commun.annuler}
              </button>
            </div>
          </form>
        )}
      </Panneau>
    </Coquille>
  );
}
